import React from 'react';
import { Rating } from 'src/types/rating';
import { RatingSubject } from 'src/enums/rating';
import { WorkType as WorkTypeEnum } from 'src/enums/work';
import {
    ActivityHeader,
    RatingSection,
    RatingItemContainer,
    RatingHeader,
    RatingLabel,
    StyledStar,
    StyledStarOutline,
    StyledRating,
    CommentContainer,
    CommentLabel,
    CommentText,
    RatingActivityHeader
} from './styled';
import { getWorkTypeName } from './utils';
import TaskTitleLink from './TaskTitleLink';

interface Props {
    isQuestRating: boolean;
    ratings: Rating[];
    user: string;
    taskTitle?: string;
    taskId?: string;
    workType?: WorkTypeEnum;
}

const RatingActivity: React.FC<Props> = ({
    isQuestRating,
    ratings,
    user,
    taskTitle,
    taskId,
    workType
}) => {
    if (isQuestRating) {
        const questDescriptionRating = ratings.find(
            (r) => r.subject === RatingSubject.QUEST_DESCRIPTION
        );
        if (questDescriptionRating) {
            return (
                <RatingSection>
                    <RatingItemContainer>
                        <RatingHeader>
                            <RatingLabel>Request description</RatingLabel>
                            <StyledRating
                                value={questDescriptionRating.score || 0}
                                readOnly
                                size="small"
                                icon={<StyledStar />}
                                emptyIcon={<StyledStarOutline />}
                            />
                        </RatingHeader>
                        {questDescriptionRating.text && (
                            <CommentContainer>
                                <CommentLabel>Comment:</CommentLabel>
                                <CommentText>
                                    {questDescriptionRating.text}
                                </CommentText>
                            </CommentContainer>
                        )}
                    </RatingItemContainer>
                </RatingSection>
            );
        }
        const codeQualityRating = ratings.find(
            (r) => r.subject === RatingSubject.QUEST_SOLUTION_CODE_QUALITY
        );
        const requirementsRating = ratings.find(
            (r) => r.subject === RatingSubject.QUEST_SOLUTION_MATCH_REQUIREMENTS
        );
        return (
            <ActivityHeader>
                <RatingSection>
                    <RatingItemContainer>
                        <RatingHeader>
                            <RatingLabel>Code quality</RatingLabel>
                            <StyledRating
                                value={codeQualityRating?.score || 0}
                                readOnly
                                size="small"
                                icon={<StyledStar />}
                                emptyIcon={<StyledStarOutline />}
                            />
                        </RatingHeader>
                        {codeQualityRating?.text && (
                            <CommentContainer>
                                <CommentLabel>Comment:</CommentLabel>
                                <CommentText>
                                    {codeQualityRating.text}
                                </CommentText>
                            </CommentContainer>
                        )}
                    </RatingItemContainer>
                    <RatingItemContainer>
                        <RatingHeader>
                            <RatingLabel>Match requirements</RatingLabel>
                            <StyledRating
                                value={requirementsRating?.score || 0}
                                readOnly
                                size="small"
                                icon={<StyledStar />}
                                emptyIcon={<StyledStarOutline />}
                            />
                        </RatingHeader>
                        {requirementsRating?.text && (
                            <CommentContainer>
                                <CommentLabel>Comment:</CommentLabel>
                                <CommentText>
                                    {requirementsRating.text}
                                </CommentText>
                            </CommentContainer>
                        )}
                    </RatingItemContainer>
                </RatingSection>
            </ActivityHeader>
        );
    }

    // Work rating
    const workTypeText = workType ? getWorkTypeName(workType) : '';
    const workDescriptionRating = ratings.find(
        (r) => r.subject === RatingSubject.WORK_DESCRIPTION
    );
    if (workDescriptionRating) {
        return (
            <>
                <ActivityHeader>
                    <strong>{workTypeText}</strong> work{' '}
                    <TaskTitleLink taskId={taskId} title={taskTitle} /> was
                    rated by <strong>{user}</strong>.
                </ActivityHeader>
                <RatingSection>
                    <RatingItemContainer>
                        <RatingHeader>
                            <RatingLabel>Work description</RatingLabel>
                            <StyledRating
                                value={workDescriptionRating.score || 0}
                                readOnly
                                size="small"
                                icon={<StyledStar />}
                                emptyIcon={<StyledStarOutline />}
                            />
                        </RatingHeader>
                        {workDescriptionRating.text && (
                            <CommentContainer>
                                <CommentLabel>Comment:</CommentLabel>
                                <CommentText>
                                    {workDescriptionRating.text}
                                </CommentText>
                            </CommentContainer>
                        )}
                    </RatingItemContainer>
                </RatingSection>
            </>
        );
    }
    const codeQualityRating = ratings.find(
        (r) =>
            r.subject === RatingSubject.WORK_SOLUTION_CODE_QUALITY ||
            r.subject === RatingSubject.WORK_REVIEW_CODE_QUALITY
    );
    const requirementsRating = ratings.find(
        (r) =>
            r.subject === RatingSubject.WORK_SOLUTION_MATCH_REQUIREMENTS ||
            r.subject === RatingSubject.WORK_REVIEW_QA_FUNCTIONALITY
    );
    return (
        <>
            <RatingActivityHeader>
                <strong>{workTypeText}</strong> work{' '}
                <TaskTitleLink taskId={taskId} title={taskTitle} /> was rated by{' '}
                <strong>{user}</strong>.
            </RatingActivityHeader>
            <ActivityHeader>
                <RatingSection>
                    <RatingItemContainer>
                        <RatingHeader>
                            <RatingLabel>Code quality</RatingLabel>
                            <StyledRating
                                value={codeQualityRating?.score || 0}
                                readOnly
                                size="small"
                                icon={<StyledStar />}
                                emptyIcon={<StyledStarOutline />}
                            />
                        </RatingHeader>
                        {codeQualityRating?.text && (
                            <CommentContainer>
                                <CommentLabel>Comment:</CommentLabel>
                                <CommentText>
                                    {codeQualityRating.text}
                                </CommentText>
                            </CommentContainer>
                        )}
                    </RatingItemContainer>
                    <RatingItemContainer>
                        <RatingHeader>
                            <RatingLabel>Match requirements</RatingLabel>
                            <StyledRating
                                value={requirementsRating?.score || 0}
                                readOnly
                                size="small"
                                icon={<StyledStar />}
                                emptyIcon={<StyledStarOutline />}
                            />
                        </RatingHeader>
                        {requirementsRating?.text && (
                            <CommentContainer>
                                <CommentLabel>Comment:</CommentLabel>
                                <CommentText>
                                    {requirementsRating.text}
                                </CommentText>
                            </CommentContainer>
                        )}
                    </RatingItemContainer>
                </RatingSection>
            </ActivityHeader>
        </>
    );
};

export default RatingActivity;
